/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react'
import { AsyncPaginate, wrapMenuList } from 'react-select-async-paginate'
import { ClearIndicator, getCustomSelectStyles, MenuList, Option, ValueContainer } from '../Select'
import { useServiceContext } from '../../root'

// Type definitions for the component
interface OptionType {
  value: any
  label: string
  type?: string
  propertyType?: string
  isFixed?: boolean
  isDisabled?: boolean
  hasBottomSeparator?: boolean
}

interface AsyncLoadResponse {
  options: OptionType[]
  hasMore: boolean
}

interface LoadOptionsParams {
  page: number
}

interface InfiniteScrollSelectProps {
  value?: OptionType | OptionType[] | null
  asyncLoadFn: (search: string, page: number) => Promise<AsyncLoadResponse>
  isFromTags?: boolean
  multi?: boolean
  id?: string
  onChange?: (option: OptionType | OptionType[] | null) => void
  [key: string]: any
  customStyles?: any
}

const WrappedMenuList = wrapMenuList(MenuList)

// Helper function to process value and set isFixed for SYSTEM tags
const processValueForTags = (
  val: OptionType | OptionType[] | null | undefined,
  isFromTags?: boolean
): OptionType | OptionType[] | null => {
  if (!val) return null
  if (isFromTags && Array.isArray(val)) {
    return val.map((item: OptionType) => {
      if (item?.type === 'SYSTEM' || item?.propertyType === 'SYSTEM') {
        return { ...item, isFixed: true }
      }
      return item
    })
  }
  return val
}

export const InfiniteScrollSelect: React.FC<InfiniteScrollSelectProps> = ({
  value,
  asyncLoadFn,
  isFromTags,
  customStyles = {},
  ...props
}) => {
  // Initialize with processed value to ensure isFixed is set on first render
  const [parsedValue, setParsedValue] = useState<OptionType | OptionType[] | null>(() =>
    processValueForTags(value, isFromTags)
  )
  const [reloadKey, setReloadKey] = useState<number>(new Date().valueOf())
  const subscriptions = useRef<(() => void)[]>([])
  const context = useServiceContext()

  // Track previous value to avoid infinite loops
  const prevValueRef = useRef(value)

  useEffect(
    function getSelectedOption() {
      // Only update if the value prop actually changed
      if (value === prevValueRef.current) {
        return
      }
      prevValueRef.current = value

      const newValue = processValueForTags(value, isFromTags)
      setParsedValue(newValue)
    },
    [value, isFromTags]
  )

  useEffect(() => {
    if (context?.bus) {
      const handleTableUpdate = () => {
        setReloadKey(new Date().valueOf())
      }

      context.bus.addEventListener('TABLE_UPDATE', handleTableUpdate)

      subscriptions.current.push(() => {
        context.bus.removeEventListener('TABLE_UPDATE', handleTableUpdate)
      })
    }

    return () => {
      subscriptions.current.forEach((sbs) => sbs())
    }
  }, [context])

  const onChange = (option: any) => {
    // Convert readonly array to mutable array if needed
    const mutableOption = Array.isArray(option) ? [...option] : option
    setParsedValue(mutableOption)
    props.onChange && props.onChange(mutableOption)
  }

  const loadOptions = async (search: string, _prevOptions: any, additional?: { page: number }) => {
    const page = additional?.page ?? 0
    const response = await asyncLoadFn(search, page)

    return {
      options: response.options,
      hasMore: response.hasMore,
      additional: {
        page: page + 1
      }
    }
  }

  const attrs = {
    value: parsedValue,
    inputId: `${props.id}-input`,
    isClearable: true,
    isSearchable: true,
    components: { WrappedMenuList, ClearIndicator, ValueContainer, Option },
    menuPortalTarget: typeof window !== 'undefined' ? window.document.body : undefined,
    styles: {
      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
      ...getCustomSelectStyles({ override: {} }),
      zIndex: 9999,
      ...customStyles
    },
    isMulti: !!props.multi,
    ...props,
    debounceTimeout: 300,
    loadOptions,
    onChange: onChange
  }

  return (
    <AsyncPaginate
      key={reloadKey}
      additional={{
        page: 0
      }}
      {...attrs}
    />
  )
}
